import React, { FunctionComponent, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from 'react-responsive';

import { RootStore, Collection } from '../app/types';
import logo from '../images/logo.svg';
import { newCollection } from '../api';
import { setShowUnpublishedArticles, setShowUnpublishedCollections } from '../app/actions';

export const PublicationsHeader: FunctionComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMedium = useMediaQuery({ minWidth: 500 });

    const [newCollectionName, setNewCollectionName] = useState("");

    const appName = useSelector((state: RootStore) => state.appName);
    const articleCount = useSelector((state: RootStore) => state.articles.length);
    const isLoggedIn = useSelector((state: RootStore) => state.loggedInUser !== null);
    const showUnpublishedCollections = useSelector((state: RootStore) => state.showUnpublishedCollections);
    const showUnpublishedArticles = useSelector((state: RootStore) => state.showUnpublishedArticles);

    const onClickNewCollection = () => {
        if (newCollectionName.length) {
            newCollection(dispatch, newCollectionName)
                .then((collection: Collection) => {
                    setNewCollectionName("");
                    navigate("/collection/" + collection.ID);
                });
        }
    };

    return (
        <>
            {/* Main Header Bar */}
            <header
                style={{
                    backgroundColor: '#2d2d2d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    minHeight: 64,
                }}
            >
                {/* Left: Logo and App Name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img
                        src={logo}
                        alt="logo"
                        onClick={() => navigate('/')}
                        style={{
                            height: 48,
                            width: 48,
                            cursor: 'pointer',
                            borderRadius: 8,
                        }}
                    />
                    {isMedium && (
                        <span
                            style={{
                                color: '#e0e0e0',
                                fontSize: '1.25rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {appName}
                        </span>
                    )}
                </div>

                {/* Right: Article Count */}
                <span style={{ color: '#9e9e9e', fontSize: '0.85rem' }}>
                    {articleCount} articles
                </span>
            </header>

            {/* Admin Toolbar (when logged in) */}
            {isLoggedIn && (
                <div
                    style={{
                        backgroundColor: '#3d3d3d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        gap: 16,
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Left: Visibility Toggles (stacked) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: '#bbb',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}>
                            <input
                                id="show-unpublished-collections"
                                type="checkbox"
                                checked={showUnpublishedCollections}
                                onChange={() => dispatch(setShowUnpublishedCollections(!showUnpublishedCollections))}
                                style={{ cursor: 'pointer' }}
                            />
                            Show All Collections
                        </label>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: '#bbb',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}>
                            <input
                                id="show-unpublished-articles"
                                type="checkbox"
                                checked={showUnpublishedArticles}
                                onChange={() => dispatch(setShowUnpublishedArticles(!showUnpublishedArticles))}
                                style={{ cursor: 'pointer' }}
                            />
                            Show All Articles
                        </label>
                    </div>

                    {/* Right: New Collection */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                            id="new-collection-title"
                            placeholder="New collection..."
                            autoComplete="off"
                            value={newCollectionName}
                            onChange={e => e.target.value.indexOf("@") < 0 ? setNewCollectionName(e.target.value) : null}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && newCollectionName.length > 0) {
                                    onClickNewCollection();
                                }
                            }}
                            style={{
                                padding: '6px 12px',
                                fontSize: '0.85rem',
                                border: '1px solid #555',
                                borderRadius: 6,
                                backgroundColor: '#4d4d4d',
                                color: '#e0e0e0',
                                width: 160,
                                outline: 'none',
                            }}
                        />
                        <button
                            id="new-collection-button"
                            onClick={onClickNewCollection}
                            disabled={newCollectionName.length === 0}
                            style={{
                                padding: '6px 12px',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                border: 'none',
                                borderRadius: 6,
                                backgroundColor: newCollectionName.length > 0 ? '#28a745' : '#555',
                                color: newCollectionName.length > 0 ? '#fff' : '#888',
                                cursor: newCollectionName.length > 0 ? 'pointer' : 'not-allowed',
                            }}
                        >
                            Create
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
